import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import "../components/index.js";
import { toast } from "../components/index.js";
import { pb } from "../api/client.js";
import { humanError } from "../api/errors.js";
import { registerPasskey } from "../api/passkeys.js";
import { webauthnSupported, devicePasskeyName, passkeyFailureMessage } from "../api/webauthn.js";
import { navigate } from "../nav.js";

/**
 * page-onboarding is the first-login welcome step (route /welcome, guarded).
 * page-login sends fresh accounts here — "fresh" meaning the auth record has
 * no display name yet, which is exactly what implicit OTP signup produces.
 * Two steps: pick a display name (a plain self-update on the users record —
 * allowed by PocketBase's default self-update rule — then re-saved into
 * authStore so the shell and later pages see it immediately), then an
 * optional "add a passkey" prompt so the next sign-in needs no email
 * round-trip. The passkey step is skippable and disappears entirely on
 * browsers without WebAuthn. Both exits land on the `next` query param
 * (carried over from the original login redirect) or /workspaces.
 */
@customElement("page-onboarding")
export class PageOnboarding extends LitElement {
  @state() private step: "name" | "passkey" = "name";
  @state() private name = "";
  @state() private submitting = false;
  /** Inline, field-level message shown under the name input. */
  @state() private fieldError = "";

  static override styles = css`
    :host {
      display: flex;
      justify-content: center;
      align-items: flex-start;
      padding-top: clamp(var(--bino-space-lg), 8vh, 6rem);
    }
    .card {
      width: 100%;
      max-width: 25rem;
      box-sizing: border-box;
      background: var(--bino-surface);
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius-lg);
      box-shadow: var(--bino-shadow-page);
      padding: var(--bino-space-xl);
    }
    h1 {
      margin: 0 0 var(--bino-space-xs);
      font-family: var(--bino-font-sans);
      font-size: 1.375rem;
      font-weight: 600;
      color: var(--bino-text);
      letter-spacing: -0.01em;
    }
    .lede {
      margin: 0 0 var(--bino-space-lg);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-md);
      line-height: 1.5;
      color: var(--bino-text-secondary);
    }
    .actions {
      margin-top: var(--bino-space-lg);
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-sm);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    // Prefill from the record: a user who lands here with a name already set
    // (e.g. via browser back) just confirms it instead of retyping.
    const name = pb.authStore.record?.name;
    if (typeof name === "string") this.name = name;
  }

  /** done leaves onboarding for the destination login originally aimed at. */
  private done() {
    const next = new URLSearchParams(location.search).get("next");
    navigate(next && next.startsWith("/") ? next : "/workspaces", { replace: true });
  }

  /**
   * skipName advances without saving a display name — the name is a nicety,
   * never a wall. Whoever skips just keeps an empty name (the shell falls
   * back to the email) and can set one later.
   */
  private skipName() {
    if (this.submitting) return;
    if (webauthnSupported()) {
      this.step = "passkey";
    } else {
      this.done();
    }
  }

  private async saveName() {
    const name = this.name.trim();
    if (name === "") {
      this.fieldError = "Enter a name.";
      return;
    }
    this.fieldError = "";
    this.submitting = true;
    try {
      const record = pb.authStore.record;
      if (!record) {
        // Session evaporated mid-onboarding; the guard will bounce to /login.
        navigate("/login", { replace: true });
        return;
      }
      const updated = await pb.collection("users").update(record.id, { name });
      // Keep the cached auth record in sync — the token is unchanged, only
      // the record payload gained the name (the shell renders from this).
      pb.authStore.save(pb.authStore.token, updated);
      if (webauthnSupported()) {
        this.step = "passkey";
      } else {
        this.done();
      }
    } catch (err) {
      const msg = humanError(err);
      this.fieldError = msg;
      toast(msg, "error");
    } finally {
      this.submitting = false;
    }
  }

  private async addPasskey() {
    if (this.submitting) return;
    this.submitting = true;
    try {
      await registerPasskey(devicePasskeyName());
      toast("Passkey added. Use it the next time you sign in.", "success");
      this.done();
    } catch (err) {
      // Dismissing the browser prompt is a "let me think" — stay on the step
      // so both Add and Skip remain available. Real failures (server
      // rejection, RP-ID mismatch, unsupported authenticator) do toast.
      const msg = passkeyFailureMessage(err);
      if (msg) toast(msg, "error");
    } finally {
      this.submitting = false;
    }
  }

  private onKey(e: KeyboardEvent) {
    if (e.key === "Enter" && !this.submitting) {
      e.preventDefault();
      void this.saveName();
    }
  }

  override render() {
    return html`
      <div class="card">
        ${this.step === "name" ? this.renderName() : this.renderPasskey()}
      </div>
    `;
  }

  private renderName() {
    return html`
      <h1>Set up your account</h1>
      <p class="lede">What should we call you?</p>
      <bn-field label="Your name" ?required=${true} error=${this.fieldError}>
        <bn-input
          id="name"
          autocomplete="name"
          placeholder="Ada Lovelace"
          ?invalid=${this.fieldError !== ""}
          ?disabled=${this.submitting}
          .value=${this.name}
          @input=${(e: CustomEvent) => {
            this.name = (e.detail as { value: string }).value;
            if (this.fieldError) this.fieldError = "";
          }}
          @keydown=${(e: KeyboardEvent) => this.onKey(e)}
        ></bn-input>
      </bn-field>
      <div class="actions">
        <bn-button
          variant="primary"
          block
          ?loading=${this.submitting}
          @click=${() => void this.saveName()}
          >Continue</bn-button
        >
        <bn-button variant="ghost" block ?disabled=${this.submitting} @click=${() => this.skipName()}
          >Skip for now</bn-button
        >
      </div>
    `;
  }

  private renderPasskey() {
    return html`
      <h1>Add a passkey?</h1>
      <p class="lede">
        Next time, sign in with your fingerprint, face or device PIN instead of an email code.
        Email codes keep working, and you can manage passkeys under Security.
      </p>
      <div class="actions">
        <bn-button
          variant="primary"
          block
          ?loading=${this.submitting}
          @click=${() => void this.addPasskey()}
          >Add passkey</bn-button
        >
        <bn-button variant="ghost" block ?disabled=${this.submitting} @click=${() => this.done()}
          >Skip for now</bn-button
        >
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-onboarding": PageOnboarding;
  }
}
